import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Clock, 
  Play, 
  Stop, 
  Coffee, 
  MapPin,
  Timer,
  SpinnerGap,
  CheckCircle,
  Warning,
  ArrowLeft,
  CalendarBlank,
  Briefcase,
  CaretRight,
  TrendUp,
  ClockCounterClockwise,
  Export,
  PencilSimple
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { timeTrackingAPI, jobsAPI, applicationsAPI } from '../api/api';
import { BreadcrumbsInline } from '../components/Breadcrumbs';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const TimeClock = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [recentEntries, setRecentEntries] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showClockOutModal, setShowClockOutModal] = useState(false);
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [gpsStatus, setGpsStatus] = useState(null);
  
  const timerRef = useRef(null);

  // Check if user is worker
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    if (userType !== 'worker') {
      navigate('/contractor-dashboard');
      return;
    }
  }, [navigate]);

  // Fetch active session
  const fetchActiveSession = useCallback(async () => {
    try {
      const response = await timeTrackingAPI.getActiveSession();
      if (response.data.success && response.data.active_session) {
        setActiveSession(response.data.active_session);
        setElapsedTime(response.data.active_session.work_minutes || 0);
      } else {
        setActiveSession(null);
      }
    } catch (error) {
      console.error('Error fetching active session:', error);
    }
  }, []);

  // Fetch assigned jobs (accepted applications)
  const fetchAssignedJobs = useCallback(async () => {
    try {
      const response = await applicationsAPI.getMyApplications();
      const apps = response.data.applications || [];
      const accepted = apps.filter(app => app.status === 'accepted');
      
      // Get job details for each
      const jobPromises = accepted.map(async (app) => {
        try {
          const jobRes = await jobsAPI.getById(app.job_id);
          return {
            ...app,
            job: jobRes.data.job
          };
        } catch {
          return null;
        }
      });
      
      const jobsWithDetails = (await Promise.all(jobPromises)).filter(Boolean);
      setAssignedJobs(jobsWithDetails);
    } catch (error) {
      console.error('Error fetching assigned jobs:', error);
    }
  }, []);

  // Fetch recent entries
  const fetchRecentEntries = useCallback(async () => {
    try {
      const response = await timeTrackingAPI.getMyEntries({ limit: 10 });
      if (response.data.success) {
        setRecentEntries(response.data.entries);
      }
    } catch (error) {
      console.error('Error fetching recent entries:', error);
    }
  }, []);

  // Fetch weekly summary
  const fetchWeeklySummary = useCallback(async () => {
    try {
      const response = await timeTrackingAPI.getWeeklySummary(0);
      if (response.data.success) {
        setWeeklySummary(response.data.week_summary);
      }
    } catch (error) {
      console.error('Error fetching weekly summary:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchActiveSession(),
        fetchAssignedJobs(),
        fetchRecentEntries(),
        fetchWeeklySummary()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchActiveSession, fetchAssignedJobs, fetchRecentEntries, fetchWeeklySummary]);

  // Timer for elapsed time
  useEffect(() => {
    if (activeSession && !activeSession.on_break) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 60000);
      
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [activeSession]);

  // Get GPS location
  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      setGpsStatus('loading');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsStatus('success');
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          setGpsStatus('error');
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  // Clock In
  const handleClockIn = async () => {
    if (!selectedJobId) {
      toast({ title: 'Select a Job', description: 'Please select a job to clock in to', variant: 'destructive' });
      return;
    }
    
    setActionLoading(true);
    try {
      let location = null;
      try {
        location = await getLocation();
      } catch (gpsError) {
        console.log('GPS not available');
      }
      
      const response = await timeTrackingAPI.clockIn({
        job_id: selectedJobId,
        latitude: location?.latitude,
        longitude: location?.longitude
      });
      
      if (response.data.success) {
        toast({ title: 'Clocked In!', description: 'Time tracking started' });
        setActiveSession(response.data.entry);
        setElapsedTime(0);
        fetchRecentEntries();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to clock in';
      toast({ title: 'Clock In Failed', description: message, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      let location = null;
      try {
        location = await getLocation();
      } catch (gpsError) {
        console.log('GPS not available');
      }
      
      const response = await timeTrackingAPI.clockOut(
        { notes: clockOutNotes || null },
        location?.latitude,
        location?.longitude
      );
      
      if (response.data.success) {
        toast({ 
          title: 'Clocked Out!', 
          description: `Total work time: ${response.data.summary.work_time}` 
        });
        setActiveSession(null);
        setElapsedTime(0);
        setShowClockOutModal(false);
        setClockOutNotes('');
        fetchRecentEntries();
        fetchWeeklySummary();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to clock out';
      toast({ title: 'Clock Out Failed', description: message, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // Start/End Break
  const handleBreak = async (action, breakType = 'rest') => {
    setActionLoading(true);
    try {
      if (action === 'start') {
        await timeTrackingAPI.startBreak(breakType);
        toast({ title: 'Break Started' });
      } else {
        const response = await timeTrackingAPI.endBreak();
        toast({ title: 'Break Ended', description: `Break was ${response.data.break_duration}` });
      }
      fetchActiveSession();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // Format time
  const formatTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  // Format datetime
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2B4C6F] to-blue-600 text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: '/worker-dashboard' },
              { label: 'Time Clock' }
            ]}
            className="mb-3"
          />
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Clock size={32} weight="bold" />
            </div>
            <div>
              <h1 className="text-3xl font-black">Time Clock</h1>
              <p className="text-white/80">Track your work hours</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Active Session or Clock In Card */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden">
          {activeSession ? (
            // Active Session
            <div>
              <div className={`p-6 ${activeSession.on_break ? 'bg-amber-500' : 'bg-emerald-500'} text-white`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white/80 mb-1">
                      {activeSession.on_break ? 'On Break' : 'Currently Working'}
                    </p>
                    <h2 className="text-xl font-black">{activeSession.job_title || 'Active Job'}</h2>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-black">{formatTime(elapsedTime)}</p>
                    {activeSession.break_minutes > 0 && (
                      <p className="text-sm text-white/80">
                        <Coffee size={14} weight="bold" className="inline mr-1" />
                        {formatTime(activeSession.break_minutes)} breaks
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* GPS Status */}
                {activeSession.gps_clock_in && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600">
                    <MapPin size={16} weight="bold" />
                    <span>GPS verified at clock-in</span>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  {activeSession.on_break ? (
                    <Button
                      onClick={() => handleBreak('end')}
                      disabled={actionLoading}
                      className="col-span-2 h-14 bg-emerald-500 hover:bg-emerald-600 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none text-lg font-bold"
                    >
                      {actionLoading ? <SpinnerGap size={24} className="animate-spin" /> : (
                        <><Play size={24} weight="bold" className="mr-2" /> End Break</>
                      )}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => handleBreak('start', 'rest')}
                        disabled={actionLoading}
                        variant="outline"
                        className="h-12 border-2 border-amber-500 text-amber-600 hover:bg-amber-50 font-bold"
                      >
                        <Coffee size={20} weight="bold" className="mr-2" /> Break
                      </Button>
                      <Button
                        onClick={() => handleBreak('start', 'lunch')}
                        disabled={actionLoading}
                        variant="outline"
                        className="h-12 border-2 border-amber-500 text-amber-600 hover:bg-amber-50 font-bold"
                      >
                        <Coffee size={20} weight="bold" className="mr-2" /> Lunch
                      </Button>
                    </>
                  )}
                  
                  {!activeSession.on_break && (
                    <Button
                      onClick={() => setShowClockOutModal(true)}
                      disabled={actionLoading}
                      className="col-span-2 h-16 bg-red-500 hover:bg-red-600 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none text-xl font-black"
                    >
                      <Stop size={28} weight="bold" className="mr-2" /> Clock Out
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Clock In Form
            <div className="p-6 space-y-4">
              <h2 className="text-xl font-black text-neutral-900 dark:text-white flex items-center gap-2">
                <Play size={24} weight="bold" className="text-emerald-500" />
                Clock In
              </h2>
              
              {assignedJobs.length === 0 ? (
                <div className="text-center py-8 bg-neutral-50 dark:bg-neutral-700 rounded-xl border-2 border-dashed border-neutral-300">
                  <Briefcase size={48} weight="light" className="mx-auto text-neutral-300 mb-2" />
                  <p className="text-neutral-500 mb-2">No active job assignments</p>
                  <Link to="/browse-jobs">
                    <Button variant="outline" className="border-2">
                      Browse Jobs <CaretRight size={16} weight="bold" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="font-bold text-neutral-900 dark:text-white">Select Job</Label>
                    <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                      <SelectTrigger className="mt-1 h-14 border-2 border-neutral-200 dark:border-neutral-600 text-lg">
                        <SelectValue placeholder="Choose a job to clock in..." />
                      </SelectTrigger>
                      <SelectContent>
                        {assignedJobs.map(app => (
                          <SelectItem key={app.job_id} value={app.job_id}>
                            <div className="flex items-center gap-2">
                              <Briefcase size={16} weight="bold" />
                              {app.job?.title || 'Untitled Job'}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                    <MapPin size={18} weight="bold" className="text-blue-500 mt-0.5" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      GPS location will be recorded for verification
                    </p>
                  </div>
                  
                  <Button
                    onClick={handleClockIn}
                    disabled={actionLoading || !selectedJobId}
                    className="w-full h-16 bg-emerald-500 hover:bg-emerald-600 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none text-xl font-black disabled:opacity-50"
                  >
                    {actionLoading ? (
                      <SpinnerGap size={28} className="animate-spin" />
                    ) : (
                      <><Play size={28} weight="bold" className="mr-3" /> Clock In</>
                    )}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Weekly Summary */}
        {weeklySummary && (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-black text-neutral-900 dark:text-white flex items-center gap-2">
                <TrendUp size={24} weight="bold" className="text-[#2B4C6F]" />
                This Week
              </h3>
              <Link to="/timesheets">
                <Button variant="outline" size="sm" className="border-2">
                  View Timesheets <CaretRight size={16} weight="bold" />
                </Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg border-2 border-neutral-200 dark:border-neutral-600">
                <p className="text-3xl font-black text-neutral-900 dark:text-white">
                  {weeklySummary.total_hours?.toFixed(1) || '0'}
                </p>
                <p className="text-sm text-neutral-500">Total Hours</p>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg border-2 border-neutral-200 dark:border-neutral-600">
                <p className="text-3xl font-black text-emerald-500">
                  {weeklySummary.total_regular_hours?.toFixed(1) || '0'}
                </p>
                <p className="text-sm text-neutral-500">Regular</p>
              </div>
              <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg border-2 border-neutral-200 dark:border-neutral-600">
                <p className="text-3xl font-black text-amber-500">
                  {weeklySummary.total_overtime_hours?.toFixed(1) || '0'}
                </p>
                <p className="text-sm text-neutral-500">Overtime</p>
              </div>
            </div>
            
            {weeklySummary.jobs?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-bold text-neutral-600 dark:text-neutral-400">By Job:</p>
                {weeklySummary.jobs.map(job => (
                  <div key={job.job_id} className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
                    <span className="font-medium text-neutral-900 dark:text-white">{job.job_title}</span>
                    <span className="text-[#2B4C6F] font-bold">{job.work_hours.toFixed(1)}h</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent Entries */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] p-6">
          <h3 className="text-lg font-black text-neutral-900 dark:text-white flex items-center gap-2 mb-4">
            <ClockCounterClockwise size={24} weight="bold" className="text-[#2B4C6F]" />
            Recent Time Entries
          </h3>
          
          {recentEntries.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <Clock size={48} weight="light" className="mx-auto text-neutral-300 mb-2" />
              <p>No time entries yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentEntries.map(entry => (
                <div 
                  key={entry._id}
                  className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg border-2 border-neutral-200 dark:border-neutral-600 hover:border-[#2B4C6F] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      entry.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'
                    }`}>
                      <Clock size={20} weight="bold" className="text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900 dark:text-white">{entry.job_title}</p>
                      <p className="text-sm text-neutral-500">
                        {formatDateTime(entry.clock_in)}
                        {entry.clock_out && ` → ${formatDateTime(entry.clock_out)}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-lg text-neutral-900 dark:text-white">
                      {formatTime(entry.work_minutes || 0)}
                    </p>
                    {entry.overtime_hours > 0 && (
                      <p className="text-sm text-amber-500 font-medium">
                        +{entry.overtime_hours.toFixed(1)}h OT
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Clock Out Modal */}
      <Dialog open={showClockOutModal} onOpenChange={setShowClockOutModal}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-neutral-800 border-2 border-black shadow-[4px_4px_0px_#000]">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Stop size={24} weight="bold" className="text-red-500" />
              Clock Out
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Work Time</p>
              <p className="text-3xl font-black text-neutral-900 dark:text-white">
                {formatTime(elapsedTime)}
              </p>
              {activeSession?.job_title && (
                <p className="text-sm text-[#2B4C6F] mt-1">{activeSession.job_title}</p>
              )}
            </div>
            
            <div>
              <Label className="font-bold">Notes (Optional)</Label>
              <Textarea
                value={clockOutNotes}
                onChange={(e) => setClockOutNotes(e.target.value)}
                placeholder="Add any notes about today's work..."
                className="mt-1 min-h-[80px] border-2"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowClockOutModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleClockOut}
              disabled={actionLoading}
              className="bg-red-500 hover:bg-red-600 border-2 border-black shadow-[2px_2px_0px_#000]"
            >
              {actionLoading ? (
                <SpinnerGap size={20} className="animate-spin" />
              ) : (
                <><CheckCircle size={20} weight="bold" className="mr-2" /> Confirm Clock Out</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeClock;
